import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Edit05, Trash01, User01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { Avatar } from "@/components/Atoms/Avatar/Avatar";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { EditAvatarModal } from "@/components/Organisms/EditAvatarModal";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAuth } from "@/contexts/AuthProvider";
import { DeleteAccountDialog } from "@/features/user-profile/DeleteAccountDialog";
import {
	leaveAfterAccountDeletion,
	rememberAccountDeletedCopy,
	showsEntraDeleteCopy,
	type DeleteAccountExit,
} from "@/features/user-profile/delete-account";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import {
	useDeleteUser,
	useDeleteUserAvatar,
	useUploadUserAvatar,
} from "@/services/user/user.mutations";
import { authStore } from "@/stores/auth-store";
import ProfileDetailsContent from "./ProfileDetailsContent";
import ProfilePreferencesContent from "./ProfilePreferencesContent";
import ProfileSecurityContent from "./ProfileSecurityContent";

export const TabSchema = z.enum(["details", "preferences", "security"]);
type Tab = z.infer<typeof TabSchema>;

// Get the route API for the authenticated route where search params are defined
const routeApi = getRouteApi("/_authenticated");

// Type for the search params from the authenticated route
type AuthenticatedSearch = {
	profileDrawer?: string;
	profileDrawerTab?: string;
};

export default function UserProfileDrawer() {
	const navigate = useNavigate();
	const search = routeApi.useSearch();
	const { t } = useAppTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [isEditAvatarOpen, setIsEditAvatarOpen] = useState(false);
	const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
	const [hasDeleteError, setHasDeleteError] = useState(false);
	// Reset query errors when route changes to prevent remount loops
	const queryErrorResetBoundary = useQueryErrorResetBoundary();

	// Get user data from auth context
	const { user, isLoading } = useAuth();
	const hasError = !user && !isLoading;

	// Avatar management
	const { src: avatarSrc } = useUserAvatarSrc(user?.id);
	const uploadAvatarMutation = useUploadUserAvatar();
	const deleteAvatarMutation = useDeleteUserAvatar();
	const deleteAccountMutation = useDeleteUser();
	const deleteAccountAction = useEntityActionAllowed({
		name: "DeleteUser",
		entityType: "User",
		entityId: user?.id,
		enabled: Boolean(user?.id),
	});
	const [isEditingMfa, setIsEditingMfa] = useState(false);

	// Handle avatar save (upload cropped image)
	const handleAvatarSave = async (croppedImage: Blob) => {
		if (!user?.id) return;
		await uploadAvatarMutation.mutateAsync({
			userId: user.id,
			file: croppedImage,
		});
	};

	// Handle avatar remove
	const handleAvatarRemove = async () => {
		if (!user?.id) return;
		await deleteAvatarMutation.mutateAsync(user.id);
	};

	const leaveSignedInApp = (exit: DeleteAccountExit) => {
		if (exit === "account-deleted") {
			return navigate({ to: "/account-deleted", replace: true });
		}
		return navigate({
			to: "/login",
			search: { reason: "access-removed" },
			replace: true,
		});
	};

	const handleDeleteAccount = async () => {
		if (!user?.id || deleteAccountMutation.isPending) return;
		setHasDeleteError(false);
		try {
			const outcome = await deleteAccountMutation.mutateAsync(user.id);
			if (outcome === "ended") {
				await leaveAfterAccountDeletion({
					kind: "access-removed",
					clearLocalSession: () => authStore.getState().clearLocalSession(),
					navigate: leaveSignedInApp,
					setLoading: (loading) => authStore.getState().setLoading(loading),
				});
				return;
			}
			rememberAccountDeletedCopy({
				title: t("userProfile.deleteAccount.success.title"),
				body: t("userProfile.deleteAccount.success.body"),
			});
			await leaveAfterAccountDeletion({
				kind: "success",
				clearLocalSession: () => authStore.getState().clearLocalSession(),
				navigate: leaveSignedInApp,
				setLoading: (loading) => authStore.getState().setLoading(loading),
			});
		} catch {
			setHasDeleteError(true);
		}
	};

	// Read currentTab from search params, default to "details"
	const currentTab = (search.profileDrawerTab as Tab) || "details";

	// Reset MFA edit state when switching away from Security tab
	useEffect(() => {
		if (currentTab !== "security") setIsEditingMfa(false);
	}, [currentTab]);

	// Get display name or construct from first/last name
	const displayName =
		user?.displayName ||
		`${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
		t("main.nav.user.name");

	// Open drawer when component mounts (route is accessed)
	useEffect(() => {
		setIsOpen(true);
	}, []);

	// Reset query error boundary on mount to prevent remount loops
	useEffect(() => {
		queryErrorResetBoundary.reset();
	}, [queryErrorResetBoundary]);

	// Handle drawer close - navigate back to parent route
	const handleClose = () => {
		setIsOpen(false);
		// Delay navigation to allow exit animation to complete (200ms)
		setTimeout(() => {
			// Build new search params without the profile drawer params
			const newSearch: AuthenticatedSearch = { ...search };
			delete newSearch.profileDrawer;
			delete newSearch.profileDrawerTab;

			navigate({
				to: ".",
				search: newSearch,
				replace: true,
			});
		}, 200);
	};

	// Handle drawer state change
	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			// If drawer is being closed, navigate back
			handleClose();
		}
	};

	// Determine drawer title and content
	const title = displayName;
	const description = t("userProfile.drawer.description");
	const tabs: Array<{ label: string; id: Tab }> = [
		{ label: t("admin.users.drawer.tabs.details"), id: "details" },
		// {
		// 	label: t("admin.nav.items.settings.preferences.label"),
		// 	id: "preferences",
		// },
		{ label: t("userProfile.security.title"), id: "security" },
	];

	const displayTabs =
		currentTab === "security" && isEditingMfa
			? [
					{
						label: t("userProfile.security.mfa.editTitle"),
						id: "security" as Tab,
					},
				]
			: tabs;

	return (
		<Drawer
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			width="40%"
			backdropPreset="transparent"
		>
			{({ close }) => (
				<>
					{deleteAccountAction.allowed ? (
						<div className="absolute top-3 right-14 z-20">
							<Dropdown.Root>
								<Dropdown.DotsButton
									aria-label={t("userProfile.deleteAccount.menu")}
								/>
								<Dropdown.Popover>
									<Dropdown.Menu>
										<Dropdown.Item
											id="delete-account"
											label={t("userProfile.deleteAccount.menu")}
											icon={Trash01}
											className="[&_span]:text-error-primary"
											onAction={() => {
												setHasDeleteError(false);
												setIsDeleteAccountOpen(true);
											}}
										/>
									</Dropdown.Menu>
								</Dropdown.Popover>
							</Dropdown.Root>
						</div>
					) : null}
					<Drawer.Header
						variant="document"
						showDivider={false}
						icon={
							<div className="relative group">
								<Avatar
									size="md"
									src={avatarSrc}
									placeholderIcon={({ className }) => (
										<User01
											className={`${className || ""}`}
											size={32}
											style={{ stroke: "var(--Brand-500, #9E77ED)" }}
										/>
									)}
									className="!bg-[#F4EBFF] !outline-none"
								/>
								{/* Edit button overlay */}
								<button
									type="button"
									onClick={() => setIsEditAvatarOpen(true)}
									className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-brand-600 text-white shadow-sm transition-all hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
									aria-label={t("userProfile.avatar.edit")}
								>
									<Edit05 className="h-3 w-3" />
								</button>
							</div>
						}
						iconSize="md"
						iconTheme="light"
						title={
							<div className="flex items-center flex-row gap-4">
								<span>{title}</span>
							</div>
						}
						description={description}
						showCloseButton={true}
						closeButtonClassName="absolute top-2 right-2 z-10"
						onClose={() => {
							close();
							handleClose();
						}}
					/>
					<Drawer.Content>
						{isLoading ? (
							<div className="flex items-center justify-center h-full min-h-[400px]">
								<LoadingIndicator type="line-simple" size="xl" supportingText />
							</div>
						) : hasError ? (
							<div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
								<Illustration type="cloud" size="sm">
									<div className="bg-error-500 rounded-full p-3">
										<AlertCircle className="size-6" />
									</div>
								</Illustration>
								<div className="flex flex-col items-center justify-center pt-4">
									<p className="text-gray-900 font-semibold text-md">
										{t("admin.organizations.errors.errorLoadingPage")}
									</p>
									<div className="w-[60%]">
										<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
											{t("admin.organizations.errors.errorLoadingContent")}
										</p>
									</div>
								</div>
							</div>
						) : (
							<div className="space-y-4">
								<NativeSelect
									aria-label={t("userProfile.drawer.title")}
									value={currentTab}
									onChange={(event) =>
										navigate({
											to: ".",
											search: {
												...search,
												profileDrawer: "open",
												profileDrawerTab: event.target.value,
											},
										})
									}
									options={displayTabs.map((tab) => ({
										label: tab.label,
										value: tab.id,
									}))}
									className="w-80 md:hidden"
								/>
								<Tabs
									selectedKey={currentTab}
									onSelectionChange={(event) =>
										navigate({
											to: ".",
											search: {
												...search,
												profileDrawer: "open",
												profileDrawerTab: event as string,
											},
										})
									}
									className="w-full max-md:hidden"
								>
									<Tabs.List type="underline" items={displayTabs} fullWidth>
										{(tab) => <Tabs.Item {...tab} />}
									</Tabs.List>
								</Tabs>
								<TabContentWrapper tabKey={currentTab}>
									{currentTab === "details" && <ProfileDetailsContent />}
									{currentTab === "preferences" && (
										<ProfilePreferencesContent />
									)}
									{currentTab === "security" && (
										<ProfileSecurityContent
											isEditingMfa={isEditingMfa}
											onEditingMfaChange={setIsEditingMfa}
										/>
									)}
								</TabContentWrapper>
							</div>
						)}
					</Drawer.Content>
					<Drawer.Footer>
						<div className="flex items-center justify-between w-full">
							<div className="flex-1" />
							<div className="flex items-center gap-3">
								{currentTab === "security" && isEditingMfa ? (
									<>
										<Button
											color="secondary"
											size="md"
											onClick={() => setIsEditingMfa(false)}
										>
											{t("common.actions.cancelEdits")}
										</Button>
										<Button
											color="primary"
											size="md"
											onClick={() => {
												// TODO: wire to MFA API when ready
												setIsEditingMfa(false);
											}}
										>
											{t("common.actions.saveEdits")}
										</Button>
									</>
								) : (
									<Button color="secondary" size="md" onClick={handleClose}>
										{t("common.actions.close")}
									</Button>
								)}
							</div>
						</div>
					</Drawer.Footer>

					{/* Avatar Edit Modal */}
					<DeleteAccountDialog
						isOpen={isDeleteAccountOpen}
						isLoading={deleteAccountMutation.isPending}
						showEntraCopy={showsEntraDeleteCopy(user?.usingFirstPartyAuth)}
						hasError={hasDeleteError}
						onCancel={() => {
							if (deleteAccountMutation.isPending) return;
							setIsDeleteAccountOpen(false);
							setHasDeleteError(false);
						}}
						onConfirm={() => {
							void handleDeleteAccount();
						}}
					/>
					<EditAvatarModal
						isOpen={isEditAvatarOpen}
						setIsOpen={setIsEditAvatarOpen}
						currentAvatarSrc={avatarSrc}
						onSave={handleAvatarSave}
						onRemove={handleAvatarRemove}
						isSaving={uploadAvatarMutation.isPending}
						isRemoving={deleteAvatarMutation.isPending}
					/>
				</>
			)}
		</Drawer>
	);
}
